import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import zipObj from 'ramda/src/zipObj';
import { Box } from '../Box';
import { Space } from '../Space';
import { useMediaSize } from '../tools/utils';
import { muiBankTheme } from '../tools/theme';
const styledSystemBreakpoints = [
    'default',
    'sm',
    'md',
    'lg',
];
const getAlignment = align => {
    switch (align) {
        case 'center':
            return 'center';
        case 'right':
            return 'flex-end';
        default:
            return 'flex-start';
    }
};
const getVerticalAlignment = alignVertical => {
    switch (alignVertical) {
        case 'center':
            return 'center';
        case 'bottom':
            return 'flex-end';
        default:
            return 'flex-start';
    }
};
const OuterBox = styled(Box) `
  ${({ marginValues, theme: { media } }) => `
    &:before {
      display: block;
      content: '';
    }

    ${media.up('sm')} {
      &:before {
        margin-top: ${marginValues.sm};
      }
    }

    ${media.up('md')} {
      &:before {
        margin-top: ${marginValues.md};
      }
    }

    ${media.up('lg')} {
      &:before {
        margin-top: ${marginValues.lg};
      }
    }
  `}
`;
const getFlexDirection = (reverse, isRowMode) => {
    const rowOrColumn = isRowMode ? 'row' : 'column';
    return `flex-direction: ${reverse ? `${rowOrColumn}-reverse` : rowOrColumn};`;
};
/**
 * Generates the style 'flex-direction: row|column[-reversed]'
 */
const BreakpointInline = styled(Box).attrs((props) => ({
    reverse: props.reverse || false,
    isRowMode: props.isRowMode,
})) `
  display: flex;
  ${({ reverse, isRowMode }) => getFlexDirection(reverse, isRowMode)}
`;
const themeSpace = muiBankTheme.space;
/**
 * Convert a number to a negative pixel value, ie 4 ==> '-4px'
 */
const toNegativePixelString = (num) => `-${num}px`;
/**
 * Given a spacing string value (ie, 'md') return a pixel value from the theme (ie, '24px')
 */
const toThemeNumber = (spaceVal) => parseInt(themeSpace[spaceVal], 10);
const Inline = ({ children, space, spaceColumn, align, alignVertical, reverse, className, breakpointWidth, }) => {
    const [isRowMode, setRowMode] = useState(false);
    const isSmDown = useMediaSize('SmDown');
    const isMdOnly = useMediaSize('MdOnly');
    const isLgUp = useMediaSize('LgUp');
    useEffect(() => {
        switch (breakpointWidth) {
            case 'sm':
                setRowMode(!isSmDown);
                break;
            case 'md':
                setRowMode(!(isMdOnly || isSmDown));
                break;
            case 'lg':
                setRowMode(isLgUp);
                break;
            default:
                setRowMode(true);
                break;
        }
    }, [isSmDown, isMdOnly, isLgUp, breakpointWidth, isRowMode]);
    const spaceArray = Array.isArray(space) ? space : [space, space, space, space];
    const negativeTopPixels = spaceArray
        .map(toThemeNumber)
        .map(number => number + 1)
        .map(toNegativePixelString);
    const negativePixelMap = zipObj(styledSystemBreakpoints, negativeTopPixels);
    const negativePixelValues = spaceArray.map(sp => {
        const pxValue = themeSpace[sp];
        return `-${pxValue}`;
    });
    const filteredChildren = React.Children.toArray(children).filter(Boolean);
    return (React.createElement(OuterBox, { marginValues: negativePixelMap, pt: "1px" },
        React.createElement(BreakpointInline, Object.assign({ ml: negativePixelValues, display: "flex", isRowMode: isRowMode, reverse: reverse, flexWrap: "wrap", className: className, "data-testid": "mui-inline-container" }, (align && { justifyContent: getAlignment(align) }), (alignVertical && {
            alignItems: getVerticalAlignment(alignVertical),
        })), React.Children.map(filteredChildren, (child) => {
            return (React.createElement(Space, { mt: isRowMode ? space : spaceColumn, ml: isRowMode ? space : spaceColumn }, child));
        }))));
};
export { Inline };
//# sourceMappingURL=Inline.js.map